import React, { useEffect, useMemo, useState } from 'react';
import {
  createPrintGroup,
  createPrinter,
  deletePrinter,
  getDevices,
  getPrintGroups,
  getPrinters,
  getSettings,
  testPrinter,
  updatePrinter,
  updateSettings,
  waitForPrintJob,
} from '../../lib/api';
import { readDeviceIdentity } from '../../lib/deviceIdentity';
import PrinterDiscovery from '../../components/PrinterDiscovery';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertTriangle,
  Bluetooth,
  FlaskConical,
  MonitorSmartphone,
  Loader2,
  Pencil,
  Plus,
  Printer,
  RefreshCw,
  Save,
  Trash2,
  Usb,
  Wifi,
} from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_PRINTER_FORM = {
  name: '',
  type: 'network',
  connection_string: '',
  paper_width: 80,
  is_default: false,
  device_id: '',
};

function formatPaperWidthLabel(width) {
  return Number(width || 80) >= 200 ? 'A4' : `${width}mm`;
}

function formatPrinterTypeLabel(type) {
  switch (type) {
    case 'system':
      return 'System';
    case 'network':
      return 'Netzwerk';
    case 'serial':
      return 'Bluetooth am Computer';
    case 'epson_epos':
      return 'Epson ePOS';
    case 'bluetooth':
      return 'Bluetooth mobil';
    case 'usb':
      return 'USB (alt)';
    default:
      return type || 'Unbekannt';
  }
}

export default function AdminPrinters() {
  const [printers, setPrinters] = useState([]);
  const [printGroups, setPrintGroups] = useState([]);
  const [devices, setDevices] = useState([]);
  const [devicesUnavailable, setDevicesUnavailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showPrinterDialog, setShowPrinterDialog] = useState(false);
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [printerForm, setPrinterForm] = useState(DEFAULT_PRINTER_FORM);
  const [newGroupName, setNewGroupName] = useState('');
  const [testStatus, setTestStatus] = useState({});
  const [printServerUrl, setPrintServerUrl] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Printers always belong to a device; legacy printers without one are listed separately.
  const printerSections = useMemo(() => {
    const deviceIds = new Set(devices.map((device) => device.id));
    const sections = devices.map((device) => ({
      key: device.id,
      device,
      printers: printers.filter((printer) => printer.device_id === device.id),
    }));
    const unassigned = printers.filter((printer) => !printer.device_id || !deviceIds.has(printer.device_id));
    if (unassigned.length > 0) sections.push({ key: 'unassigned', device: null, printers: unassigned });
    return sections;
  }, [devices, printers]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [printersData, groupsData, settings, devicesResult] = await Promise.all([
        getPrinters(),
        getPrintGroups(),
        getSettings(),
        getDevices().then((result) => ({ ok: true, result }), () => ({ ok: false })),
      ]);
      setPrinters(printersData);
      setPrintGroups(groupsData);
      setDevices(devicesResult.ok ? devicesResult.result.devices || [] : []);
      setDevicesUnavailable(!devicesResult.ok);
      setPrintServerUrl(settings.print_server_url || '');
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Druckerdaten konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePrintServerUrl = async () => {
    setSavingUrl(true);
    try {
      await updateSettings({ print_server_url: printServerUrl });
      toast.success('Print-Server-URL gespeichert');
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingUrl(false);
    }
  };

  const handleTestPrinter = async (printer) => {
    setTestStatus((current) => ({ ...current, [printer.id]: 'testing' }));
    try {
      const result = await testPrinter(printer.id);
      if (result.job_id) {
        // Printed by the printer's device; wait for its confirmation.
        const job = await waitForPrintJob(result.job_id);
        if (job?.status === 'done') {
          setTestStatus((current) => ({ ...current, [printer.id]: 'ok' }));
          toast.success(`Testdruck erfolgreich: ${printer.name}`);
        } else {
          setTestStatus((current) => ({ ...current, [printer.id]: 'error' }));
          toast.error(job?.status === 'failed'
            ? `Testdruck fehlgeschlagen: ${job.error || 'Unbekannter Fehler'}`
            : `Keine Rückmeldung von „${result.device_name}“. Ist dort die Kasse geöffnet?`);
        }
      } else if (result.success) {
        setTestStatus((current) => ({ ...current, [printer.id]: 'ok' }));
        toast.success(`Testdruck erfolgreich: ${printer.name}`);
      } else {
        setTestStatus((current) => ({ ...current, [printer.id]: 'error' }));
        toast.error(`Testdruck fehlgeschlagen: ${result.error || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      setTestStatus((current) => ({ ...current, [printer.id]: 'error' }));
      toast.error(error?.response?.data?.detail || 'Testdruck fehlgeschlagen');
    }
  };

  const handleOpenPrinterDialog = async (printer = null, prefill = null) => {
    if (printer) {
      setEditingPrinter(printer);
      setPrinterForm({
        name: printer.name || '',
        type: printer.type || 'system',
        connection_string: printer.connection_string || '',
        paper_width: Number(printer.paper_width || 80),
        is_default: Boolean(printer.is_default),
        device_id: printer.device_id || '',
      });
    } else {
      // Printers found by the search hang on this computer, so preselect it when it is a registered device.
      const thisDevice = readDeviceIdentity();
      const defaultDeviceId = devices.some((device) => device.id === thisDevice?.id)
        ? thisDevice.id
        : devices.length === 1 ? devices[0].id : '';
      setEditingPrinter(null);
      setPrinterForm({ ...DEFAULT_PRINTER_FORM, device_id: defaultDeviceId, ...(prefill || {}) });
    }
    setShowPrinterDialog(true);
  };

  const handleSavePrinter = async () => {
    if (!printerForm.name.trim()) {
      toast.error('Bitte Namen eingeben');
      return;
    }
    if (!printerForm.connection_string.trim()) {
      toast.error('Bitte Verbindung eingeben');
      return;
    }
    if (!printerForm.device_id) {
      toast.error('Bitte das Gerät wählen, an dem der Drucker hängt');
      return;
    }
    try {
      const payload = {
        ...printerForm,
        connection_string: printerForm.connection_string.trim(),
      };
      if (editingPrinter) {
        await updatePrinter(editingPrinter.id, payload);
        toast.success('Drucker aktualisiert');
      } else {
        await createPrinter(payload);
        toast.success('Drucker erstellt');
      }
      setShowPrinterDialog(false);
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDeletePrinter = async (printerId) => {
    if (!window.confirm('Drucker wirklich löschen?')) return;
    try {
      await deletePrinter(printerId);
      toast.success('Drucker gelöscht');
      await loadData();
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error('Bitte Namen eingeben');
      return;
    }
    try {
      await createPrintGroup(newGroupName);
      toast.success('Druckgruppe erstellt');
      setShowGroupDialog(false);
      setNewGroupName('');
      await loadData();
    } catch {
      toast.error('Fehler beim Erstellen');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'network':
        return <Wifi className="w-5 h-5" />;
      case 'bluetooth':
        return <Bluetooth className="w-5 h-5" />;
      case 'usb':
        return <Usb className="w-5 h-5" />;
      default:
        return <Printer className="w-5 h-5" />;
    }
  };

  const StatusDot = ({ status }) => {
    if (!status || status === 'idle') return <span className="w-2.5 h-2.5 rounded-full bg-slate-300 inline-block" />;
    if (status === 'testing') return <Loader2 className="w-3 h-3 animate-spin text-slate-400 inline-block" />;
    if (status === 'ok') return <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />;
    return <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-printers">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Drucker</h1>
          <p className="text-slate-500">{printers.length} Drucker an {devices.length} Geräten</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
          <Button variant="outline" onClick={() => setShowGroupDialog(true)}>
            Druckgruppe
          </Button>
          <Button className="bg-[#1a2f38] hover:bg-[#2a4048]" onClick={() => handleOpenPrinterDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Neuer Drucker
          </Button>
        </div>
      </div>

      <Card className="shadow-sm border-l-4 border-l-emerald-500">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Drucker gehören zu Geräten</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-slate-500">
          <p>
            Jeder Drucker hängt an einem Gerät (Kasse, Bar-PC, Kellner-Handy). Dieses Gerät druckt seine Aufträge selbst.
            Eingerichtet wird am besten direkt am Gerät: <span className="font-medium text-slate-700">POS → Einstellungen → Dieses Gerät</span>.
            Dort findet der Druck-Agent Netzwerk- und USB-Drucker automatisch, und Bluetooth-Drucker werden gekoppelt.
          </p>
          {devicesUnavailable ? (
            <p className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-4 h-4" />
              Geräteverwaltung nicht verfügbar: Datenbank-Migration 19_devices_and_print_jobs.sql fehlt.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Auf diesem Computer gefunden</CardTitle>
        </CardHeader>
        <CardContent>
          <PrinterDiscovery
            existingPrinters={printers}
            onSelect={(values) => handleOpenPrinterDialog(null, values)}
          />
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Print-Server-URL</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500 mb-3">
            Optionaler Legacy-Sidecar. Für lokale USB- und Systemdrucker auf diesem Rechner ist das nicht nötig.
          </p>
          <div className="flex gap-2">
            <Input
              value={printServerUrl}
              onChange={(event) => setPrintServerUrl(event.target.value)}
              placeholder="http://192.168.1.50:6001"
              className="font-mono"
            />
            <Button
              onClick={handleSavePrintServerUrl}
              disabled={savingUrl}
              className="bg-[#1a2f38] hover:bg-[#2a4048] shrink-0"
            >
              {savingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span className="ml-2">Speichern</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {printerSections.map((section) => (
      <div key={section.key} className="space-y-3">
        <div className="flex items-center gap-2">
          {section.device ? <MonitorSmartphone className="w-5 h-5 text-slate-500" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
          <h2 className="font-semibold text-[#1a2f38]">{section.device ? section.device.name : 'Ohne Gerät'}</h2>
          {section.device ? (
            <span className={`text-xs px-2 py-0.5 rounded ${section.device.online ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
              {section.device.online ? 'Online' : 'Offline'}
            </span>
          ) : (
            <span className="text-xs text-amber-700">Bitte einem Gerät zuordnen, sonst druckt nur ein lokal laufendes Backend.</span>
          )}
        </div>
        {section.printers.length === 0 ? <p className="text-sm text-slate-400">Keine Drucker an diesem Gerät.</p> : null}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {section.printers.map((printer) => {
          return (
            <Card key={printer.id} className="shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                    {getTypeIcon(printer.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold truncate">{printer.name}</h3>
                      {printer.is_default ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded shrink-0">Standard</span>
                      ) : null}
                      <StatusDot status={testStatus[printer.id]} />
                    </div>
                    <p className="text-sm text-slate-500">{formatPrinterTypeLabel(printer.type)}</p>
                    <p className="text-sm text-slate-400 font-mono truncate">{printer.connection_string || 'Nicht konfiguriert'}</p>
                    <p className="text-sm text-slate-400">{formatPaperWidthLabel(printer.paper_width)}</p>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Testdruck"
                      disabled={testStatus[printer.id] === 'testing'}
                      onClick={() => handleTestPrinter(printer)}
                    >
                      {testStatus[printer.id] === 'testing'
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <FlaskConical className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleOpenPrinterDialog(printer)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500"
                      onClick={() => handleDeletePrinter(printer.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      </div>
      ))}

      {printers.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="py-12 text-center">
            <Printer className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">Keine Drucker konfiguriert</p>
            <Button onClick={() => handleOpenPrinterDialog()} className="mt-4 bg-[#1a2f38]">
              Ersten Drucker hinzufügen
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Druckgruppen</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {printGroups.map((group) => (
              <div key={group.id} className="px-4 py-2 bg-slate-100 rounded-lg">
                <span className="font-medium">{group.name}</span>
              </div>
            ))}
            {printGroups.length === 0 ? (
              <p className="text-slate-400">Keine Druckgruppen (z.B. Küche, Bar, Kasse)</p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card className="border-l-4 border-l-blue-500">
        <CardContent className="p-4">
          <h3 className="font-bold mb-2">Drucker-Typen</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Wifi className="w-5 h-5 text-blue-500" />
              <div>
                <p className="font-medium">Netzwerk</p>
                <p className="text-slate-500">IP:Port (z.B. 192.168.1.100:9100), über den Druck-Agent</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-blue-500" />
              <div>
                <p className="font-medium">System / USB</p>
                <p className="text-slate-500">Druckerwarteschlange des Geräts, über den Druck-Agent</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Usb className="w-5 h-5 text-blue-500" />
              <div>
                <p className="font-medium">Epson ePOS</p>
                <p className="text-slate-500">Epson TM mit ePOS, druckt direkt aus dem Browser</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Bluetooth className="w-5 h-5 text-blue-500" />
              <div>
                <p className="font-medium">Bluetooth mobil</p>
                <p className="text-slate-500">Persönlicher Drucker am Handy (Android + Chrome), am Gerät koppeln</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showPrinterDialog} onOpenChange={setShowPrinterDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingPrinter ? 'Drucker bearbeiten' : 'Neuen Drucker hinzufügen'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-4 max-h-[75vh] overflow-y-auto pr-1">
            {!editingPrinter && !printerForm.connection_string ? (
              <PrinterDiscovery
                existingPrinters={printers}
                onSelect={(values) => setPrinterForm((current) => ({ ...current, ...values }))}
              />
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Name *</Label>
                <Input
                  value={printerForm.name}
                  onChange={(event) => setPrinterForm({ ...printerForm, name: event.target.value })}
                  placeholder="z.B. Bondrucker vorne"
                  autoFocus
                />
              </div>
              <div className="md:col-span-2">
                <Label>Gerät *</Label>
                <Select value={printerForm.device_id || undefined} onValueChange={(value) => setPrinterForm({ ...printerForm, device_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder={devices.length ? 'Gerät wählen, an dem der Drucker hängt' : 'Noch kein Gerät registriert'} />
                  </SelectTrigger>
                  <SelectContent>
                    {devices.map((device) => (
                      <SelectItem key={device.id} value={device.id}>{device.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {devices.length === 0 ? (
                  <p className="mt-1 text-xs text-amber-700">Zuerst am Gerät registrieren: POS → Einstellungen → Dieses Gerät.</p>
                ) : null}
              </div>
              <div>
                <Label>Typ</Label>
                <Select value={printerForm.type} onValueChange={(value) => setPrinterForm({ ...printerForm, type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="network">Netzwerk (IP)</SelectItem>
                    <SelectItem value="system">System / USB-Warteschlange</SelectItem>
                    <SelectItem value="serial">Bluetooth am Computer</SelectItem>
                    <SelectItem value="epson_epos">Epson ePOS (ohne Agent)</SelectItem>
                    <SelectItem value="bluetooth">Bluetooth mobil (am Gerät koppeln)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Verbindung</Label>
                <Input
                  value={printerForm.connection_string}
                  onChange={(event) => setPrinterForm({ ...printerForm, connection_string: event.target.value })}
                  placeholder={
                    printerForm.type === 'system'
                      ? 'Queue-Name, z.B. Printer_printer_80'
                      : printerForm.type === 'serial'
                        ? '/dev/cu.BLTH'
                      : printerForm.type === 'network'
                        ? '192.168.1.100:9100'
                        : printerForm.type === 'epson_epos'
                          ? '192.168.1.60'
                          : 'Wird beim Koppeln am Gerät gesetzt'
                  }
                  readOnly={printerForm.type === 'bluetooth'}
                  className="font-mono"
                />
              </div>
              <div>
                <Label>Papierbreite</Label>
                <Select
                  value={String(printerForm.paper_width)}
                  onValueChange={(value) => setPrinterForm({ ...printerForm, paper_width: parseInt(value, 10) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="58">58mm</SelectItem>
                    <SelectItem value="80">80mm</SelectItem>
                    <SelectItem value="210">A4</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={printerForm.is_default}
                    onChange={(event) => setPrinterForm({ ...printerForm, is_default: event.target.checked })}
                    className="w-4 h-4"
                  />
                  <span>Standard-Drucker</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowPrinterDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSavePrinter} className="bg-[#1a2f38] hover:bg-[#2a4048]">
                {editingPrinter ? 'Speichern' : 'Hinzufügen'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neue Druckgruppe</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Name</Label>
              <Input
                value={newGroupName}
                onChange={(event) => setNewGroupName(event.target.value)}
                placeholder="z.B. Küche, Bar"
              />
            </div>
            <Button onClick={handleCreateGroup} className="w-full bg-[#1a2f38]">
              Erstellen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
